import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import LogLayout from "../components/LogLayout";
import { useNavigate } from "react-router-dom";
import {
  Bell, Check, Trash2, Filter, Settings, FileText, Truck,
  CheckCircle2, Wallet, CreditCard, AlertTriangle, XCircle,
  BarChart3, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const ICON_MAP = {
  "file-text": FileText, "truck": Truck, "check-circle": CheckCircle2,
  "wallet": Wallet, "credit-card": CreditCard, "alert-triangle": AlertTriangle,
  "x-circle": XCircle, "bar-chart": BarChart3, "bell": Bell,
};
const SEVERITY_COLORS = {
  info: "bg-blue-500/100/15 text-blue-400 border-blue-500/20",
  success: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  error: "bg-red-500/100/15 text-red-400 border-red-500/20",
  critical: "bg-red-600/20 text-red-300 border-red-500/30",
};
const SEVERITY_DOT = {
  info: "bg-blue-400", success: "bg-emerald-400", error: "bg-red-400", critical: "bg-red-500/100 animate-pulse",
};

export default function LogNotificationsPage() {
  const [notifs, setNotifs] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filter) params.severity = filter;
      const res = await axios.get(`${API}/log/notifications`, { headers: getHeaders(), params });
      setNotifs(res.data.notifications || []);
      setTotal(res.data.total || 0);
      setUnread(res.data.unread || 0);
    } catch { }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await axios.put(`${API}/log/notifications/${id}/read`, {}, { headers: getHeaders() });
    load();
  };

  const markAllRead = async () => {
    await axios.put(`${API}/log/notifications/read-all`, {}, { headers: getHeaders() });
    toast.success("Tout marque comme lu");
    load();
  };

  const deleteNotif = async (id) => {
    await axios.delete(`${API}/log/notifications/${id}`, { headers: getHeaders() });
    load();
  };

  const loadPrefs = async () => {
    const res = await axios.get(`${API}/log/notifications/preferences`, { headers: getHeaders() });
    setPrefs(res.data.preferences);
    setShowPrefs(true);
  };

  const savePrefs = async () => {
    await axios.put(`${API}/log/notifications/preferences`, prefs, { headers: getHeaders() });
    toast.success("Preferences sauvegardees");
    setShowPrefs(false);
  };

  const handleClick = (notif) => {
    if (notif.status === "unread") markRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  return (
    <LogLayout title="Centre de notifications" subtitle={`${unread} non lue(s) sur ${total}`}>
      <div data-testid="log-notifications-page" className="space-y-4">
        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            {["", "info", "success", "error", "critical"].map(s => (
              <button key={s} onClick={() => { setFilter(s); setLoading(true); }}
                data-testid={`notif-filter-${s || "all"}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  filter === s ? "bg-blue-600 text-white" : "bg-slate-900 text-slate-400 border border-white/5 hover:border-white/10"
                }`}>
                {s === "" ? "Tous" : s === "critical" ? "Critiques" : s === "error" ? "Erreurs" : s === "success" ? "Succes" : "Info"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {unread > 0 && (
              <Button size="sm" variant="outline" onClick={markAllRead} className="text-xs border-white/10 text-slate-400 hover:text-white gap-1.5" data-testid="mark-all-read">
                <Check className="w-3.5 h-3.5" /> Tout marquer lu
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={loadPrefs} className="text-xs border-white/10 text-slate-400 hover:text-white gap-1.5" data-testid="notif-prefs-btn">
              <Settings className="w-3.5 h-3.5" /> Preferences
            </Button>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>
        ) : notifs.length === 0 ? (
          <div className="text-center py-16 bg-slate-900/60 rounded-xl border border-white/5">
            <Bell className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Aucune notification</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifs.map(n => {
              const IconComp = ICON_MAP[n.icon] || Bell;
              const severityColor = SEVERITY_COLORS[n.severity] || SEVERITY_COLORS.info;
              const dotColor = SEVERITY_DOT[n.severity] || "bg-blue-400";
              return (
                <div key={n.id} data-testid={`notif-item-${n.id}`}
                  className={`border rounded-xl p-4 transition-all cursor-pointer hover:border-blue-500/20 ${
                    n.status === "unread" ? "bg-blue-500/100/5 border-blue-500/15" : "bg-slate-900/60 border-white/5"
                  }`}
                  onClick={() => handleClick(n)}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${severityColor.split(" ")[0]}`}>
                      <IconComp className={`w-4 h-4 ${severityColor.split(" ")[1]}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {n.status === "unread" && <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dotColor}`} />}
                        <p className={`text-sm font-medium ${n.status === "unread" ? "text-white" : "text-slate-300"}`}>{n.title}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${severityColor}`}>{n.severity}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-slate-600 mt-1">{new Date(n.created_at).toLocaleString("fr-FR")}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      {n.status === "unread" && (
                        <button onClick={() => markRead(n.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-emerald-400">
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => deleteNotif(n.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400 hover:text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {n.link && <ChevronRight className="w-4 h-4 text-slate-600" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preferences Modal */}
        {showPrefs && prefs && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="notif-prefs-modal">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md">
              <div className="p-5 border-b border-white/5">
                <h2 className="text-base font-bold text-white">Preferences de notifications</h2>
                <p className="text-xs text-slate-500 mt-1">Configurez comment vous recevez vos notifications</p>
              </div>
              <div className="p-5 space-y-4">
                <TogglePref label="Email" desc="Recevoir les alertes par email" checked={prefs.email_enabled} onChange={v => setPrefs({...prefs, email_enabled: v})} testId="pref-email" />
                <TogglePref label="SMS" desc="Recevoir les alertes par SMS (necessite Twilio)" checked={prefs.sms_enabled} onChange={v => setPrefs({...prefs, sms_enabled: v})} testId="pref-sms" />
                <TogglePref label="WhatsApp" desc="Recevoir les alertes via WhatsApp" checked={prefs.whatsapp_enabled} onChange={v => setPrefs({...prefs, whatsapp_enabled: v})} testId="pref-whatsapp" />
                <TogglePref label="Critiques uniquement" desc="Canaux externes pour alertes critiques uniquement" checked={prefs.critical_only_channels} onChange={v => setPrefs({...prefs, critical_only_channels: v})} testId="pref-critical-only" />
                <div className="border-t border-white/5 pt-4">
                  <p className="text-xs text-slate-400 mb-2">Heures de silence</p>
                  <div className="flex items-center gap-2">
                    <input type="time" value={prefs.quiet_hours_start || "22:00"} onChange={e => setPrefs({...prefs, quiet_hours_start: e.target.value})}
                      className="bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
                    <span className="text-slate-500 text-xs">a</span>
                    <input type="time" value={prefs.quiet_hours_end || "07:00"} onChange={e => setPrefs({...prefs, quiet_hours_end: e.target.value})}
                      className="bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
                  </div>
                </div>
              </div>
              <div className="p-5 border-t border-white/5 flex gap-2">
                <Button onClick={savePrefs} className="bg-blue-600 hover:bg-blue-500/100 text-white flex-1" data-testid="save-prefs">Sauvegarder</Button>
                <Button variant="outline" onClick={() => setShowPrefs(false)} className="border-white/10 text-slate-400">Annuler</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LogLayout>
  );
}

function TogglePref({ label, desc, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-white">{label}</p>
        <p className="text-[10px] text-slate-500">{desc}</p>
      </div>
      <button data-testid={testId} onClick={() => onChange(!checked)}
        className={`w-10 h-6 rounded-full transition-colors flex items-center p-0.5 ${checked ? "bg-blue-600" : "bg-slate-700"}`}>
        <div className={`w-5 h-5 bg-white rounded-full transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
      </button>
    </div>
  );
}
